import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import api, { resolveImg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Anchor, Sparkles, FolderKanban, Lightbulb, CalendarDays, Settings, LogOut, Menu, X, Ship, Shield, Radar, Building2, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const LINKS = [
  { label: "Dashboard", path: "/dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { label: "Inventory", path: "/inventory", icon: Anchor, testid: "nav-inventory" },
  { label: "Create Campaign", path: "/create-campaign", icon: Sparkles, testid: "nav-create-campaign", highlight: true },
  { label: "Campaign Library", path: "/campaigns", icon: FolderKanban, testid: "nav-campaigns" },
  { label: "Opportunities", path: "/opportunities", icon: Lightbulb, testid: "nav-opportunities" },
  { label: "Buyer Intelligence", path: "/buyer-intelligence", icon: Radar, testid: "nav-buyer-intelligence" },
  { label: "Calendar", path: "/calendar", icon: CalendarDays, testid: "nav-calendar" },
  { label: "Settings", path: "/settings", icon: Settings, testid: "nav-settings" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("sidebar_collapsed") === "1");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dealership, setDealership] = useState(null);
  const initials = (user?.name || user?.email || "D").slice(0, 2).toUpperCase();

  useEffect(() => {
    api.get("/dealership").then(({ data }) => setDealership(data)).catch(() => {});
  }, []);

  const toggleCollapse = () => setCollapsed((c) => { localStorage.setItem("sidebar_collapsed", c ? "0" : "1"); return !c; });

  const NavItems = ({ compact, onClick }) => (
    <nav className="flex flex-col gap-1 px-2.5">
      {LINKS.map((l) => {
        const Icon = l.icon;
        return (
          <NavLink key={l.path} to={l.path} onClick={onClick} data-testid={l.testid}
            title={compact ? l.label : undefined}
            className={({ isActive }) =>
              `group relative flex items-center ${compact ? "justify-center" : "gap-3"} px-3 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ` +
              (isActive
                ? "bg-gold text-navy shadow-lg shadow-gold/20"
                : l.highlight ? "text-gold hover:bg-white/10" : "text-slate-300 hover:text-white hover:bg-white/10")}>
            <Icon className="w-5 h-5 shrink-0" />
            {!compact && <span className="truncate">{l.label}</span>}
            {compact && (
              <span className="pointer-events-none absolute left-full ml-3 z-50 whitespace-nowrap rounded-md bg-navy-light text-white text-xs font-semibold px-2.5 py-1.5 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg border border-white/10">
                {l.label}
              </span>
            )}
          </NavLink>
        );
      })}
    </nav>
  );

  const Brand = ({ compact }) => (
    <div className={`flex items-center ${compact ? "justify-center" : "gap-2.5"} px-4 h-16 shrink-0 cursor-pointer`} onClick={() => navigate("/dashboard")} data-testid="brand-logo">
      <div className="w-9 h-9 rounded-lg gold-gradient flex items-center justify-center shadow-md shrink-0"><Ship className="w-5 h-5 text-navy" /></div>
      {!compact && (dealership?.logo ? (
        <div className="bg-white rounded-lg px-2.5 py-1.5 flex items-center shadow-sm" data-testid="brand-dealer-logo">
          <img src={resolveImg(dealership.logo)} alt={dealership?.name || "logo"} className="h-6 max-w-[130px] object-contain" />
        </div>
      ) : (
        <div className="leading-tight min-w-0">
          <div className="font-serif text-base font-bold text-white tracking-tight truncate max-w-[150px]" data-testid="brand-dealer-name">{dealership?.name || "Marine Ad AI"}</div>
          <div className="text-[10px] uppercase tracking-widest text-gold/80 -mt-0.5">Campaign Studio</div>
        </div>
      ))}
    </div>
  );

  const UserMenu = ({ compact }) => (
    <div className="mt-auto p-2.5 border-t border-white/10">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className={`w-full flex items-center ${compact ? "justify-center" : "gap-2.5"} px-2 py-2 rounded-xl hover:bg-white/10 transition-colors outline-none`} data-testid="user-menu-trigger" title={compact ? (user?.name || "Account") : undefined}>
            <div className="w-9 h-9 rounded-full bg-gold/90 text-navy flex items-center justify-center font-bold text-sm shrink-0">{initials}</div>
            {!compact && (
              <div className="text-left min-w-0">
                <div className="text-sm font-semibold text-white truncate">{user?.name || "Dealer"}</div>
                <div className="text-[11px] text-slate-400 truncate">{user?.email}</div>
              </div>
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" side="top" className="w-56 bg-white">
          <DropdownMenuLabel className="truncate">
            <div className="font-semibold text-navy">{user?.name || "Dealer"}</div>
            <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {["owner", "admin"].includes(user?.role) && (
            <DropdownMenuItem data-testid="nav-admin-console" onClick={() => navigate("/admin")} className="cursor-pointer text-navy font-semibold">
              <Shield className="w-4 h-4 mr-2 text-gold" /> Admin Console
            </DropdownMenuItem>
          )}
          <DropdownMenuItem data-testid="logout-btn" onClick={logout} className="text-red-600 focus:text-red-600 cursor-pointer">
            <LogOut className="w-4 h-4 mr-2" /> Log out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <ExecLocationBanner />
      <div className="flex flex-1 min-h-0">
        {/* Desktop sidebar */}
        <aside className={`hidden lg:flex flex-col sticky top-0 h-screen navy-gradient border-r-2 border-gold/40 transition-[width] duration-300 ease-in-out ${collapsed ? "w-[76px]" : "w-64"}`} data-testid="desktop-sidebar" data-collapsed={collapsed}>
          <Brand compact={collapsed} />
          <div className="flex-1 overflow-y-auto py-3"><NavItems compact={collapsed} /></div>
          <div className="px-2.5 pb-1">
            <button onClick={toggleCollapse} data-testid="sidebar-collapse-toggle"
              className={`w-full flex items-center ${collapsed ? "justify-center" : "gap-2"} px-3 py-2 rounded-xl text-xs font-semibold text-slate-300 hover:text-white hover:bg-white/10 transition-colors`}
              title={collapsed ? "Expand navigation" : "Collapse navigation"}>
              {collapsed ? <PanelLeftOpen className="w-5 h-5" /> : <><PanelLeftClose className="w-5 h-5" /> <span>Collapse</span></>}
            </button>
          </div>
          <UserMenu compact={collapsed} />
        </aside>

        {/* Mobile slide-out drawer */}
        {mobileOpen && (
          <div className="lg:hidden fixed inset-0 z-50" data-testid="mobile-drawer">
            <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} data-testid="mobile-drawer-overlay" />
            <aside className="absolute left-0 top-0 h-full w-72 navy-gradient border-r-2 border-gold/40 flex flex-col shadow-2xl animate-in slide-in-from-left duration-200">
              <div className="flex items-center justify-between pr-2">
                <Brand compact={false} />
                <button onClick={() => setMobileOpen(false)} className="text-white p-2" data-testid="mobile-drawer-close"><X className="w-6 h-6" /></button>
              </div>
              <div className="flex-1 overflow-y-auto py-3"><NavItems compact={false} onClick={() => setMobileOpen(false)} /></div>
              <UserMenu compact={false} />
            </aside>
          </div>
        )}

        <div className="flex-1 min-w-0 flex flex-col">
          {/* Mobile top bar */}
          <header className="lg:hidden sticky top-0 z-40 glass-navy border-b-2 border-gold/60">
            <div className="flex items-center justify-between h-14 px-4">
              <button className="text-white" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-toggle"><Menu className="w-6 h-6" /></button>
              <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/dashboard")}>
                <div className="w-8 h-8 rounded-lg gold-gradient flex items-center justify-center"><Ship className="w-4 h-4 text-navy" /></div>
                <span className="font-serif text-base font-bold text-white truncate max-w-[160px]">{dealership?.name || "Marine Ad AI"}</span>
              </div>
              <div className="w-8 h-8 rounded-full bg-gold/90 text-navy flex items-center justify-center font-bold text-xs" onClick={() => setMobileOpen(true)} data-testid="mobile-avatar">{initials}</div>
            </div>
          </header>

          <main className="flex-1 min-w-0 w-full max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}

function ExecLocationBanner() {
  const { exec, loadExec } = useAuth();
  const navigate = useNavigate();
  if (!exec || !exec.has_group_access || !exec.active_location) return null;
  const current = (exec.locations || []).find((l) => l.dealer_id === exec.active_location);
  const others = (exec.locations || []).filter((l) => l.dealer_id !== exec.active_location);

  const goExec = async () => {
    try { await api.post("/executive/exit-location"); } catch {}
    await loadExec();
    navigate("/executive");
  };
  const switchTo = async (dealerId) => {
    try { await api.post("/executive/enter-location", { dealer_id: dealerId }); } catch {}
    await loadExec();
    navigate("/dashboard");
    window.location.reload();
  };

  return (
    <div className="bg-gold text-navy" data-testid="exec-location-banner">
      <div className="px-4 sm:px-6 py-1.5 flex items-center justify-between gap-3 text-xs sm:text-sm">
        <div className="flex items-center gap-2 min-w-0">
          <Building2 className="w-4 h-4 shrink-0" />
          <span className="font-bold">{exec.group?.group_name}</span>
          <span className="opacity-70">Current Location:</span>
          <span className="font-semibold truncate">{current?.name || "Location"}</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {others.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger className="font-semibold underline underline-offset-2 outline-none" data-testid="exec-switch-location">Switch location</DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-white">
                {others.map((l) => (
                  <DropdownMenuItem key={l.dealer_id} onClick={() => switchTo(l.dealer_id)} className="cursor-pointer" data-testid={`exec-switch-${l.dealer_id}`}>{l.name}</DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <button onClick={goExec} className="font-bold flex items-center gap-1 hover:opacity-80" data-testid="exec-return-view"><Building2 className="w-3.5 h-3.5" /> Executive View</button>
        </div>
      </div>
    </div>
  );
}
